import React from 'react';
import { motion } from 'framer-motion';

const Contact: React.FC = () => {
  return (
    <section id="contact" className="relative py-20 px-6" style={{ backgroundColor: '#fefdf8' }}>
      {/* Background Elements */}
      <div className="absolute inset-0">
        {/* Parchment grain texture */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
                           radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)`
        }}></div>
        
        {/* Shimmer gradient */}
        <div className="absolute inset-0 opacity-20" style={{
          background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(255, 255, 255, 0.2) 50%, rgba(229, 229, 229, 0.1) 100%)'
        }}></div>
        
        {/* Corner glow accents */}
        <div className="absolute top-0 left-0 w-96 h-96 rounded-full blur-3xl opacity-20" style={{
          background: 'radial-gradient(circle, rgba(212, 175, 55, 0.3) 0%, transparent 70%)'
        }}></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-20" style={{
          background: 'radial-gradient(circle, rgba(212, 175, 55, 0.3) 0%, transparent 70%)'
        }}></div>
        
        {/* Subtle vignette */}
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0, 0, 0, 0.05) 100%)'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-12 text-center"
        >
          <h2 className="text-4xl font-serif text-amber-700 mb-4">Get in Touch</h2>
          <p className="text-gray-600 mb-6 font-light">
            Have questions or ready to start your project? Reach out to us and we'll get back to you as
            soon as possible.
          </p>
          <div className="max-w-2xl mx-auto">
            <p className="text-lg text-gray-700 font-light italic leading-relaxed">
              "A brand isn’t what they see. It’s what they remember when nothing else remains."
            </p>
          </div>
        </motion.div>

        <motion.div 
          className="max-w-2xl mx-auto rounded-2xl p-8 relative overflow-hidden"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(212, 175, 55, 0.2)',
            boxShadow: '0 8px 32px rgba(212, 175, 55, 0.1)'
          }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Glass card shimmer effect */}
          <div className="absolute inset-0 opacity-30" style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(212, 175, 55, 0.1) 50%, rgba(255, 255, 255, 0.3) 100%)'
          }}></div>
          
          <div className="space-y-6 relative z-10">
            <h3 className="text-2xl text-amber-700 mb-6 text-center font-serif">Send Us a Message</h3>
            <form className="space-y-6">
              <motion.input
                type="text"
                placeholder="Enter your name"
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-amber-400 focus:outline-none transition-all duration-300 font-light"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
                whileFocus={{
                  boxShadow: "0 0 20px rgba(212, 175, 55, 0.3)",
                  borderColor: "#d4af37"
                }}
                aria-label="Name"
              />
              <motion.input
                type="email"
                placeholder="Enter your email"
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-amber-400 focus:outline-none transition-all duration-300 font-light"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
                whileFocus={{
                  boxShadow: "0 0 20px rgba(212, 175, 55, 0.3)",
                  borderColor: "#d4af37"
                }}
                aria-label="Email"
              />
              <motion.input
                type="text"
                placeholder="What is this regarding?"
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-amber-400 focus:outline-none transition-all duration-300 font-light"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
                whileFocus={{
                  boxShadow: "0 0 20px rgba(212, 175, 55, 0.3)",
                  borderColor: "#d4af37"
                }}
                aria-label="Subject"
              />
              <motion.textarea
                placeholder="Your message here..."
                rows={6}
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-amber-400 focus:outline-none transition-all duration-300 font-light resize-none"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
                whileFocus={{
                  boxShadow: "0 0 20px rgba(212, 175, 55, 0.3)",
                  borderColor: "#d4af37"
                }}
                aria-label="Message"
              />
              <motion.button
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: "0 0 30px rgba(212, 175, 55, 0.5)",
                }}
                whileTap={{ scale: 0.98 }}
                animate={{
                  boxShadow: [
                    "0 0 20px rgba(212, 175, 55, 0.3)",
                    "0 0 30px rgba(212, 175, 55, 0.4)",
                    "0 0 20px rgba(212, 175, 55, 0.3)",
                  ]
                }}
                transition={{
                  boxShadow: {
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }
                }}
                className="w-full px-8 py-4 rounded-lg font-medium transition-all duration-300 relative overflow-hidden"
                style={{
                  backgroundColor: 'transparent',
                  border: '2px solid #d4af37',
                  color: '#92400e',
                  backdropFilter: 'blur(10px)'
                }}
                type="submit"
              >
                <span className="relative z-10">Send Message</span>
                <div className="absolute inset-0 bg-gradient-to-r from-amber-100/20 to-amber-200/20 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
              </motion.button>
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;